import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, ArrowUp, ArrowDown, Loader2, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

const inputCls =
  "w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-3 text-sm text-[#1D1D1F] placeholder:text-[#86868B] focus:outline-none focus:border-[#2C3E35] transition-colors duration-200";

export const FaqsManager = ({ onUnauthorized }) => {
  const [faqs, setFaqs] = useState(null);
  const [draft, setDraft] = useState({ question: "", answer: "" });
  const [busy, setBusy] = useState("");

  const handleErr = (e, msg) => {
    if (e.response?.status === 401) onUnauthorized();
    else toast.error(e.response?.data?.detail || msg);
  };

  const load = () => {
    axios
      .get(`${API}/admin/faqs`, { headers: auth() })
      .then((r) => setFaqs(r.data))
      .catch((e) => handleErr(e, "Could not load FAQs"));
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) {
      toast.error("Both question and answer are required");
      return;
    }
    setBusy("create");
    try {
      await axios.post(`${API}/admin/faqs`, draft, { headers: auth() });
      setDraft({ question: "", answer: "" });
      toast.success("FAQ added");
      load();
    } catch (e) {
      handleErr(e, "Failed to add FAQ");
    } finally {
      setBusy("");
    }
  };

  const save = async (f) => {
    if (!f.question.trim() || !f.answer.trim()) {
      toast.error("Both question and answer are required");
      return;
    }
    setBusy(f.id);
    try {
      await axios.put(`${API}/admin/faqs/${f.id}`, { question: f.question, answer: f.answer, order: f.order }, { headers: auth() });
      toast.success("FAQ saved");
    } catch (e) {
      handleErr(e, "Failed to save FAQ");
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ permanently?")) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, { headers: auth() });
      setFaqs((prev) => prev.filter((f) => f.id !== id));
      toast.success("FAQ removed");
    } catch (e) {
      handleErr(e, "Failed to delete FAQ");
    } finally {
      setBusy("");
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= faqs.length) return;
    const a = faqs[index];
    const b = faqs[target];
    setBusy(a.id);
    try {
      await Promise.all([
        axios.put(`${API}/admin/faqs/${a.id}`, { question: a.question, answer: a.answer, order: b.order }, { headers: auth() }),
        axios.put(`${API}/admin/faqs/${b.id}`, { question: b.question, answer: b.answer, order: a.order }, { headers: auth() }),
      ]);
      load();
    } catch (e) {
      handleErr(e, "Failed to reorder");
    } finally {
      setBusy("");
    }
  };

  const setField = (id, key, value) =>
    setFaqs((prev) => prev.map((f) => (f.id === id ? { ...f, [key]: value } : f)));

  if (!faqs) return <p className="text-sm text-[#86868B]">Loading…</p>;

  return (
    <div data-testid="faqs-manager">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">FAQs</h1>
        <p className="text-sm text-[#86868B]">
          These appear in the "Curious about water from air?" section on the home page — and feed
          Google's FAQ rich results. Keep answers factual and concise.
        </p>
      </div>

      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-8">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#86868B] mb-4">Add a new FAQ</p>
        <input
          data-testid="faq-new-question"
          className={`${inputCls} mb-3`}
          placeholder="Question"
          value={draft.question}
          onChange={(e) => setDraft({ ...draft, question: e.target.value })}
        />
        <textarea
          data-testid="faq-new-answer"
          rows={3}
          className={`${inputCls} resize-none mb-4`}
          placeholder="Answer"
          value={draft.answer}
          onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
        />
        <button
          data-testid="faq-add-button"
          onClick={create}
          disabled={busy === "create"}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1D1D1F] text-white text-xs font-medium hover:bg-black disabled:opacity-60 transition-colors duration-200"
        >
          {busy === "create" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add FAQ
        </button>
      </div>

      {faqs.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <HelpCircle className="w-8 h-8 text-[#86868B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#86868B]">No FAQs yet — add your first one above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div
              key={f.id}
              data-testid={`faq-row-${f.id}`}
              className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            >
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    data-testid={`faq-move-up-${f.id}`}
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || !!busy}
                    aria-label="Move up"
                    className="p-1.5 rounded-full text-[#86868B] hover:text-[#1D1D1F] hover:bg-black/5 disabled:opacity-30 transition-colors duration-200"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`faq-move-down-${f.id}`}
                    onClick={() => move(i, 1)}
                    disabled={i === faqs.length - 1 || !!busy}
                    aria-label="Move down"
                    className="p-1.5 rounded-full text-[#86868B] hover:text-[#1D1D1F] hover:bg-black/5 disabled:opacity-30 transition-colors duration-200"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    data-testid={`faq-question-${f.id}`}
                    className={`${inputCls} mb-3 font-medium`}
                    value={f.question}
                    onChange={(e) => setField(f.id, "question", e.target.value)}
                  />
                  <textarea
                    data-testid={`faq-answer-${f.id}`}
                    rows={3}
                    className={`${inputCls} resize-none`}
                    value={f.answer}
                    onChange={(e) => setField(f.id, "answer", e.target.value)}
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      data-testid={`faq-save-${f.id}`}
                      onClick={() => save(f)}
                      disabled={busy === f.id}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2C3E35] text-white text-xs font-medium hover:bg-[#223129] disabled:opacity-60 transition-colors duration-200"
                    >
                      {busy === f.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Save
                    </button>
                    <button
                      data-testid={`faq-delete-${f.id}`}
                      onClick={() => remove(f.id)}
                      disabled={busy === f.id}
                      aria-label="Delete FAQ"
                      className="p-2 rounded-full text-[#86868B] hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
